import { useState } from "react";
import {
  FormField,
  FieldType,
  FieldVisibility,
  signatureStrokesKey,
  CARRIER_PRODUCT_FILTER_OPTIONS,
  carrierMatchesProductType,
} from "@aam/shared";
import { useFormStore } from "../store/formStore";
import { useCarrierField } from "../context/CarrierFieldContext";
import { useFormValidation } from "../context/FormValidationContext";
import { maskSSN } from "../utils/encryption";
import { PhoneField } from "./fields/PhoneField";
import { StateCombobox } from "./fields/StateCombobox";
import { FileDropZone } from "./fields/FileDropZone";
import { CheckboxGroupField } from "./fields/CheckboxGroupField";
import { SignatureCanvas } from "./fields/SignatureCanvas";
import { Input, Label, Textarea, FieldHelp, FieldError } from "./ui/Input";
import { AutofillFieldNote } from "./AutofillFieldNote";
import { cn } from "../lib/cn";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface FormFieldInputProps {
  field: FormField;
}

interface UploadedFileRef {
  fileId: string;
  fileName: string;
  size: number;
}

export function FormFieldInput({ field }: FormFieldInputProps) {
  const carrierField = useCarrierField();
  const store = useFormStore();
  const { fieldErrors, clearFieldError, setUploading } = useFormValidation();
  const value = store.formData[field.id] ?? "";
  const [focused, setFocused] = useState(false);
  const [ssnVisible, setSsnVisible] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploadingLocal] = useState(false);

  if (field.visibility === FieldVisibility.HIDDEN) return null;

  const errorMessage = fieldErrors[field.id] ?? uploadError;
  const controlId = `field-${field.id}`;
  const labelId = `${controlId}-label`;
  const helpId = field.helpText ? `${controlId}-help` : undefined;
  const errorId = errorMessage ? `${controlId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  const handleChange = (newValue: unknown) => {
    store.updateFormData(field.id, newValue);
    if (field.encrypted) store.markFieldEncrypted(field.id);
    clearFieldError(field.id);
    setUploadError(null);
  };

  const uploadFile = async (file: File) => {
    setUploadingLocal(true);
    setUploading(true);
    setUploadError(null);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("fieldId", field.id);
      const res = await fetch(`${API_BASE}/api/uploads`, { method: "POST", body });
      const json = await res.json().catch(() => ({ success: false, error: "Upload failed" }));
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Upload failed");
      }
      const uploaded = json.data as UploadedFileRef;
      handleChange({
        fileId: uploaded.fileId,
        fileName: uploaded.fileName,
        size: uploaded.size,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingLocal(false);
      setUploading(false);
    }
  };

  const renderField = () => {
    switch (field.type) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
        return (
          <Input
            id={controlId}
            type={field.type === FieldType.EMAIL ? "email" : "text"}
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            autoComplete={field.autoComplete ?? (field.type === FieldType.EMAIL ? "email" : "off")}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={describedBy}
          />
        );

      case FieldType.PHONE:
        return (
          <PhoneField
            id={controlId}
            value={value as string}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={describedBy}
          />
        );

      case FieldType.SSN:
        return (
          <div>
            <div className="relative">
              <Input
                id={controlId}
                type={ssnVisible ? "text" : "password"}
                placeholder="•••-••-••••"
                value={value as string}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
                  handleChange(digits);
                }}
                maxLength={9}
                required={field.required}
                className="pr-10"
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                aria-invalid={Boolean(errorMessage)}
                aria-describedby={describedBy}
              />
              <button
                type="button"
                onClick={() => setSsnVisible((v) => !v)}
                className="absolute right-1 top-1/2 min-h-10 min-w-10 -translate-y-1/2 text-content-muted hover:text-content-primary transition-colors"
                aria-label={ssnVisible ? "Hide SSN" : "Show SSN"}
              >
                {ssnVisible ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {(value as string) && !focused && !ssnVisible && (
              <FieldHelp className="font-mono">{maskSSN(value as string)}</FieldHelp>
            )}
          </div>
        );

      case FieldType.DATE:
        return (
          <Input
            id={controlId}
            type="date"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={describedBy}
          />
        );

      case FieldType.COMBOBOX:
        return (
          <StateCombobox
            id={controlId}
            value={value as string}
            onChange={handleChange}
            options={field.options ?? []}
            placeholder={field.placeholder}
            required={field.required}
            invalid={Boolean(errorMessage)}
            describedBy={describedBy}
          />
        );

      case FieldType.SELECT:
        return (
          <select
            id={controlId}
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className={cn(
              "w-full px-4 py-2.5 rounded-xl bg-surface-overlay border border-border-default",
              "text-content-primary text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            )}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={describedBy}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case FieldType.RADIO:
        return (
          <div className="flex gap-3 flex-wrap" role="radiogroup" aria-labelledby={labelId} aria-describedby={describedBy} aria-invalid={Boolean(errorMessage)}>
            {field.options?.map((opt) => {
              const checked = value === opt.value;
              return (
                <label
                  key={opt.value}
                  className={cn(
                    "flex items-center gap-2.5 px-4 py-2.5 rounded-xl border cursor-pointer transition-all text-sm select-none",
                    checked
                      ? "bg-accent/20 border-accent/60 text-content-primary"
                      : "bg-surface-overlay border-border-default text-content-secondary hover:border-border-strong"
                  )}
                >
                  <input
                    id={`${controlId}-${opt.value}`}
                    type="radio"
                    name={field.id}
                    value={opt.value}
                    checked={checked}
                    onChange={() => handleChange(opt.value)}
                    className="peer sr-only"
                  />
                  <div
                    className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--focus-ring)]",
                      checked ? "border-accent" : "border-border-default"
                    )}
                  >
                    {checked && <div className="w-2 h-2 rounded-full bg-accent" />}
                  </div>
                  {opt.label}
                </label>
              );
            })}
          </div>
        );

      case FieldType.CHECKBOX:
        return (
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              id={controlId}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleChange(e.target.checked)}
              required={field.required}
              className="peer sr-only"
              aria-describedby={describedBy}
              aria-invalid={Boolean(errorMessage)}
            />
            <div
              className={cn(
                "mt-0.5 w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-all peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--focus-ring)]",
                value ? "bg-accent border-accent" : "border-border-default group-hover:border-border-strong"
              )}
            >
              {value && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-sm text-content-secondary leading-relaxed">{field.label}</span>
          </label>
        );

      case FieldType.CHECKBOX_GROUP: {
        // The carrier picker gets a product-type pre-filter and greys out
        // carriers the agent already holds a contract with.
        const isCarrierPicker = field.id === "selectedCarriers";
        return (
          <CheckboxGroupField
            value={(value as string[]) || []}
            onChange={handleChange}
            options={field.options ?? []}
            gridCols={field.gridCols}
            required={field.required}
            labelledBy={labelId}
            describedBy={describedBy}
            invalid={Boolean(errorMessage)}
            categories={isCarrierPicker ? CARRIER_PRODUCT_FILTER_OPTIONS : undefined}
            matchesCategory={isCarrierPicker ? carrierMatchesProductType : undefined}
            disabledOptions={isCarrierPicker ? carrierField.unavailable : undefined}
          />
        );
      }

      case FieldType.TEXTAREA:
        return (
          <Textarea
            id={controlId}
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            rows={4}
            className="resize-none"
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={describedBy}
          />
        );

      case FieldType.FILE:
        return (
          <div className="space-y-2">
            <FileDropZone
              id={controlId}
              value={(value as UploadedFileRef | File | null) || null}
              onChange={(file) => {
                if (!file) {
                  handleChange(null);
                  return;
                }
                void uploadFile(file);
              }}
              accept={field.accept}
              label={field.label}
              required={field.required}
              describedBy={describedBy}
              invalid={Boolean(errorMessage)}
            />
            {uploading && <FieldHelp>Uploading…</FieldHelp>}
          </div>
        );

      case FieldType.SIGNATURE:
        return (
          <SignatureCanvas
            id={controlId}
            value={value as string}
            onChange={handleChange}
            onStrokesChange={(captured) =>
              store.updateFormData(signatureStrokesKey(field.id), captured)
            }
            describedBy={describedBy}
            invalid={Boolean(errorMessage)}
          />
        );

      default:
        return null;
    }
  };

  const selfLabeled =
    field.type === FieldType.CHECKBOX ||
    field.type === FieldType.CHECKBOX_GROUP ||
    field.type === FieldType.SIGNATURE;

  return (
    <div>
      {!selfLabeled && (
        <Label htmlFor={controlId} id={labelId}>
          {field.label}
          {field.required && <span className="text-status-rejected-text ml-1">*</span>}
        </Label>
      )}
      {field.type === FieldType.CHECKBOX_GROUP && (
        <p id={labelId} className="text-sm font-medium text-content-primary mb-3">
          {field.label}
          {field.required && <span className="text-status-rejected-text ml-1">*</span>}
        </p>
      )}
      {field.type === FieldType.SIGNATURE && (
        <p id={labelId} className="text-sm font-medium text-content-primary mb-3">
          {field.label}
          {field.required && <span className="text-status-rejected-text ml-1">*</span>}
        </p>
      )}
      {renderField()}
      {field.helpText && <FieldHelp id={helpId}>{field.helpText}</FieldHelp>}
      {errorMessage && <FieldError id={errorId} role="alert">{errorMessage}</FieldError>}
      <AutofillFieldNote field={field} />
      {field.encrypted && (
        <FieldHelp className="text-accent flex items-center gap-1.5">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Encrypted before transmission
        </FieldHelp>
      )}
    </div>
  );
}

export { FieldVisibility };
